import React from 'react';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { injectIntl, defineMessages, FormattedMessage } from 'react-intl';
import { Form, Input, Message, Icon, Label as BaseLabel } from 'semantic-ui-react';
import { Modal, FormLabel, Button } from '../../../components';
import { login } from '../../../actions/user';
import { setShowArchive } from '../../../actions/salon';

const intlMessages = defineMessages({
    userNameRequired: {
        id: 'pages.login.form.userNameError',
        defaultMessage: 'Enter your username',
    },
    passwordRequired: {
        id: 'pages.login.form.passwordError',
        defaultMessage: 'Enter your password',
    },
    existingsError: {
        id: 'pages.login.form.existingsError',
        defaultMessage: 'User with specified username does not exists',
    },
    passwordValidError: {
        id: 'pages.login.form.passwordValidError',
        defaultMessage: 'Password does not match',
    },
    defaultErrorMessage: {
        id: 'pages.login.form.defaultErrorMessage',
        defaultMessage: 'Unable to authenticate',
    },
    noSalonError: {
        id: 'pages.login.form.noSalonError',
        defaultMessage: 'Your account is not assigned to any salon. Please contact your administrator.',
    },
});

export const LOGIN_QUERY = gql`
    mutation login($input: UserLoginInput!) {
        login(input: $input) {
            token
            role
            user {
                id
                ... on Administrator {
                    firstName
                    lastName
                    salon {
                        id
                        administrator {
                            id
                        }
                    }
                }
                ... on Supplier {
                    firstName
                    lastName
                }
                ... on Master {
                    firstName
                    lastName
                    salon {
                        id
                        administrator {
                            id
                        }
                    }
                }
            }
        }
    }
`;

const ModalActions = styled(Modal.Actions)`
    padding-left: 1.5em !important;
    padding-right: 1.5em !important;
`;

const SubmitButton = styled(Button)`
    margin: 0px !important;
    padding-top: 1em !important;
    padding-bottom: 1em !important;
    text-transform: uppercase !important;
`;

const ForgotPasswordLink = styled.div`
    margin-top: 14px;
    text-align: center;
`;

function LoginForm({ showResetPasswordForm, intl }) {
    const dispatch = useDispatch();
    const history = useHistory();
    const [loginMutation] = useMutation(LOGIN_QUERY);
    const {
        control,
        handleSubmit,
        setError,
        formState: { errors, isSubmitting, isDirty },
    } = useForm({ defaultValues: { username: '', password: '' } });

    const onSubmit = async ({ username, password }) => {
        try {
            const { data } = await loginMutation({
                variables: { input: { username, password, role: 'ADMIN' } },
            });
            const { token, user, role } = data.login;
            let userProps, url;
            if (role === 'SUPPLIER') {
                userProps = { id: user.id, token, role };
                url = '/supplier/seminars';
            } else if (!user.salon) {
                setError('root', {
                    message: intl.formatMessage(intlMessages.noSalonError),
                });
                return;
            } else {
                userProps = {
                    id: user.id,
                    token,
                    salon: user.salon.id,
                    role,
                    isOwner: user.id === user.salon.administrator.id,
                    isMaster: role === 'MASTER',
                };
                url = '/salon/appointments';
            }
            dispatch(setShowArchive(false));
            dispatch(login(userProps));
            history.push(url);
        } catch (error) {
            const graphQLError = error.graphQLErrors && error.graphQLErrors[0];
            if (graphQLError) {
                switch (graphQLError.name) {
                    case 'UserNotFound':
                        setError('username', {
                            message: intl.formatMessage(intlMessages.existingsError),
                        });
                        return;
                    case 'InvalidPassword':
                        setError('password', {
                            message: intl.formatMessage(intlMessages.passwordValidError),
                        });
                        return;
                    default:
                        setError('root', {
                            message:
                                graphQLError.message ||
                                intl.formatMessage(intlMessages.defaultErrorMessage),
                        });
                        return;
                }
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    return (
        <Modal.Context>
            <Modal.Content>
                {errors.root && (
                    <Message error>
                        <Icon name="warning sign" />
                        {errors.root.message}
                    </Message>
                )}
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Form.Field error={!!errors.username}>
                        <FormLabel>
                            <FormattedMessage
                                id="pages.login.form.username"
                                defaultMessage="E-mail or username"
                            />
                        </FormLabel>
                        <Controller
                            name="username"
                            control={control}
                            rules={{
                                required: intl.formatMessage(intlMessages.userNameRequired),
                            }}
                            render={({ field: { ref: _ref, ...field } }) => (
                                <Input {...field} type="text" size="big" fluid />
                            )}
                        />
                        {errors.username && (
                            <BaseLabel color="red" pointing basic>
                                {errors.username.message}
                            </BaseLabel>
                        )}
                    </Form.Field>
                    <Form.Field error={!!errors.password}>
                        <FormLabel>
                            <FormattedMessage
                                id="pages.login.form.password"
                                defaultMessage="Password"
                            />
                        </FormLabel>
                        <Controller
                            name="password"
                            control={control}
                            rules={{
                                required: intl.formatMessage(intlMessages.passwordRequired),
                            }}
                            render={({ field: { ref: _ref, ...field } }) => (
                                <Input {...field} type="password" size="big" fluid />
                            )}
                        />
                        {errors.password && (
                            <BaseLabel color="red" pointing basic>
                                {errors.password.message}
                            </BaseLabel>
                        )}
                    </Form.Field>
                </Form>
            </Modal.Content>
            <ModalActions>
                <SubmitButton
                    onClick={handleSubmit(onSubmit)}
                    disabled={isSubmitting || !isDirty}
                    loading={isSubmitting}
                    primary
                    fluid
                >
                    <FormattedMessage
                        id="pages.login.button.signin"
                        defaultMessage="Sign in"
                    />
                </SubmitButton>
                <ForgotPasswordLink>
                    <a
                        href="#forgot"
                        onClick={() => {
                            setTimeout(() => showResetPasswordForm(true), 0);
                        }}
                    >
                        <FormattedMessage
                            id="pages.login.button.reset"
                            defaultMessage="Forgot your password?"
                        />
                    </a>
                </ForgotPasswordLink>
            </ModalActions>
        </Modal.Context>
    );
}

export default injectIntl(LoginForm);
